import { useEffect } from 'react';

import { useFormDataQuery } from '@/api/getters';
import { useFormMutation, useSubmitStepMutation } from '@/api/setters';
import { Stages, Steps } from '@/api/types';
import ProtectBadge from '@/components/ui/ProtectBadge';
import ReceiveBadge from '@/components/ui/ReceiveBadge';
import { formMutationDto } from '@/dto/formMutationDto';
import { useAdditionalInfoContext } from '@/features/v2/lib/additionalInfo';
import { useStepper } from '@/features/v2/lib/stepper';
import SummaryModal from '@/features/v2/summaryForms/components/stages/SummaryModal';
import BankInfo from '@/features/v2/summaryForms/surveyOne/BankInfo';
import PersonDataVehicle from '@/features/v2/summaryForms/surveyTwo/PersonDataVehicle';
import { StepForm } from '@/features/v2/vehicle-damage/StepForm';
import { scrollToTop } from '@/helpers/scrollToTop';

import { useLingui } from '@lingui/react';
import { useFormContext } from 'react-hook-form';

export const StepSummary1 = () => {
  const { handleSubmit } = useFormContext();
  const { step, nextStep, innerObject, innerStep, stepTag, setStepTag } =
    useStepper();
  const { mutate } = useFormMutation();
  const { data } = useFormDataQuery();
  const submitStep = useSubmitStepMutation();

  return (
    <StepForm
      onSubmit={handleSubmit((data) => {
        nextStep();
        if (stepTag === Steps.TRANSFER_DETAILS) {
          submitStep.mutateAsync(Steps.OWNER_DETAILS).then(() => {
            setStepTag(Steps.OWNER_DETAILS);
          });
        }
        mutate({
          ...formMutationDto({
            form_data: { ...data },
            stepper: {
              step: step + 1,
              innerStep: { ...innerObject, [step]: innerStep + 1 },
            },
          }),
        });
        scrollToTop(10);
      })}
      nextButton={{
        uniqTestId: 'step-summary-1',
      }}
      submitButton={{
        uniqTestId: 'step-summary-1-submit',
      }}
      prevButton={{
        uniqTestId: 'step-summary-1-prev',
      }}
    >
      {step >= 5 && (
        <div className="flex flex-col gap-1.5 rounded-[10px] border-t-[6px] border-blue-600 bg-gray-100 px-3.5 py-3 md:gap-2 md:rounded-lg md:p-6">
          <ReceiveBadge amount={data?.offer.surcharge_summary ?? 0} />
          <BankInfo />
          <ProtectBadge />
        </div>
      )}
    </StepForm>
  );
};

export const StepSummary2 = () => {
  const { handleSubmit } = useFormContext();
  const { step, innerObject, innerStep } = useStepper();
  const { mutate } = useFormMutation();
  const { i18n } = useLingui();
  const { setSummaryModal, modalSummary, toggleModalSummary } =
    useAdditionalInfoContext();
  const { data } = useFormDataQuery();

  const isFinished = data?.stage === Stages.STAGE_IV_FINISHED;

  useEffect(() => {
    if (isFinished && !modalSummary) {
      setSummaryModal(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <StepForm
      onSubmit={handleSubmit(async (data) => {
        mutate({
          ...formMutationDto({
            form_data: { ...data },
            stepper: {
              step: step,
              innerStep: { ...innerObject, [step]: innerStep },
            },
          }),
        });
        toggleModalSummary();
      })}
      submitButton={{
        label: i18n.t('Generuj dokumenty'),
        isDisabledProp: isFinished,
        uniqTestId: 'step-summary-2-submit',
      }}
      isLastStep={true}
      nextStageOnSubmit={false}
      nextButton={{
        uniqTestId: 'step-summary-2',
      }}
      prevButton={{
        uniqTestId: 'step-summary-2-prev',
      }}
    >
      {step >= 6 && <PersonDataVehicle />}
      <SummaryModal open={modalSummary} toggle={toggleModalSummary} />
    </StepForm>
  );
};
